import { Button, Divider, Flex, Group, Image, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { get } from 'lodash';
import { useMemo } from 'react';

import { deleteIcon } from '@assets';
import { MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { classes } from '@styles';

interface TSDTransportEquipmentProps {
  form: UseFormReturnType<any>;
  disabled: boolean;
  transportEquipmentPath?: string;
}

export const TSDTransportEquipment: React.FC<TSDTransportEquipmentProps> = ({ form, disabled, transportEquipmentPath = 'transportEquipment' }) => {
  const codelistSystems = useCodeListSystem({});

  const transportEquipment = useMemo(() => get(form.values, transportEquipmentPath), [form.values, transportEquipmentPath]);

  const addRow = () => {
    const transportEq = get(form.values, transportEquipmentPath) || [];
    form.setFieldValue(transportEquipmentPath, [
      ...transportEq,
      {
        containerIdentificationNumber: '',
        containerPackedStatus: '',
        numberOfSeals: '',
        seal: [],
      },
    ]);
  };

  const removeRow = (rowIdx: number) => {
    const transportEq = get(form.values, transportEquipmentPath) || [];
    transportEq.splice(rowIdx, 1);
    form.setFieldValue(transportEquipmentPath, [...transportEq]);
  };

  const addSeal = (rowIdx: number) => {
    const transportEq = get(form.values, transportEquipmentPath) || [];
    const seal = transportEq[rowIdx].seal || [];
    form.setFieldValue(`${transportEquipmentPath}.${rowIdx}.seal`, [...seal, { identifier: '' }]);
  };

  const removeSeal = (rowIdx: number, sealIdx: number) => {
    const transportEq = get(form.values, transportEquipmentPath) || [];
    const seal = transportEq[rowIdx].seal || [];
    seal.splice(sealIdx, 1);
    form.setFieldValue(`${transportEquipmentPath}.${rowIdx}.seal`, [...seal]);
  };

  const renderFormRow = (rowIdx: number, transportEq: any) => {
    const containerPath = (equipmentIdx: number) => `${transportEquipmentPath}.${equipmentIdx}`;
    const sealPath = (equipmentIdx: number, sealIdx: number) => `${transportEquipmentPath}.${equipmentIdx}.seal.${sealIdx}.identifier`;

    return (
      <Stack key={`${(transportEquipment || []).length}-${rowIdx}`} mb={16} gap="xs">
        <Flex mb={10}>
          <TextInput
            {...form.getInputProps(`${containerPath(rowIdx)}.containerIdentificationNumber`)}
            label={`Container ${rowIdx + 1}`}
            disabled={disabled}
            mr={16}
          />
          {/* todo_codes: CL709 */}
          <MultiLangCodes
            {...form.getInputProps(`${containerPath(rowIdx)}.containerPackedStatus`)}
            label={`Container status ${rowIdx + 1}`}
            code="CL709"
            disabled={disabled}
            system={codelistSystems}
            mr={16}
          />
          <TextInput
            {...form.getInputProps(`${containerPath(rowIdx)}.numberOfSeals`)}
            label={`Container seals count ${rowIdx + 1}`}
            disabled={disabled}
            mr={16}
          />

          {!disabled && (
            <Button
              variant="transparent"
              mt={5}
              onClick={(event) => {
                event.preventDefault();
                removeRow(rowIdx);
              }}
              className={classes.cursorPointer}
            >
              <img src={deleteIcon} alt="Remove Row" />
            </Button>
          )}
        </Flex>

        {(transportEq.seal || []).map((seal: any, sealIdx: number) => (
          <Flex mb={10} ml={16} key={`${transportEq.seal.length}-${sealIdx}`} align="center">
            <TextInput {...form.getInputProps(sealPath(rowIdx, sealIdx))} label={`Seal ${sealIdx + 1}`} disabled={disabled} data-pw={`seal-input-${sealIdx}`} />
            {!disabled && (
              <Button
                variant="transparent"
                onClick={(event) => {
                  event.preventDefault();
                  removeSeal(rowIdx, sealIdx);
                }}
              >
                <Image src={deleteIcon} alt="Remove Seal" className={classes.cursorPointer} />
              </Button>
            )}
          </Flex>
        ))}

        {!disabled && (
          <Flex align={'center'}>
            <Group>
              <Button
                onClick={(event) => {
                  event.preventDefault();
                  addSeal(rowIdx);
                }}
                variant="outline"
                size="lg"
                data-pw={`add-seal-btn-${rowIdx}`}
              >
                Add Seal
              </Button>
            </Group>
          </Flex>
        )}

        {rowIdx < transportEquipment?.length - 1 && <Divider mt="lg" />}
      </Stack>
    );
  };

  return (
    <Stack>
      {(transportEquipment || []).map((transportEq, rowIdx) => renderFormRow(rowIdx, transportEq))}
      {!disabled && (
        <Flex align={'center'}>
          <Group>
            <Button
              onClick={(event) => {
                event.preventDefault();
                addRow();
              }}
              variant="outline"
              size="lg"
              data-pw="add-container-btn"
            >
              Add Container and/or Seal
            </Button>
          </Group>
        </Flex>
      )}
    </Stack>
  );
};
